import { supabase } from '../../../config/supabase';
import { formatDateFR } from '../../shared/utils/helpers';
import { escapeForHtml } from '../../shared/utils/escapeHtml';

export const printInvoice = async (invoiceOriginal, settingsRaw) => {
  let doc = invoiceOriginal || {};
  let agencyId = (typeof window !== 'undefined' && window.__AGENCY_ID__) || null;

  // If this is a manual invoice or rental with an ID in Supabase, fetch fresh data if needed
  if (doc.id && !doc.isManual) {
    try {
      const { data: freshRental } = await supabase
        .from('rentals')
        .select('*')
        .eq('id', doc.id)
        .maybeSingle();
      if (freshRental) {
        doc = { id: freshRental.id, ...(freshRental.data || {}) };
        agencyId = freshRental.agency_id || agencyId;
      }
    } catch (_) {}
  } else if (doc.id && doc.isManual) {
    try {
      const { data: freshInvoice } = await supabase
        .from('invoices')
        .select('*')
        .eq('id', doc.id)
        .maybeSingle();
      if (freshInvoice) {
        doc = { id: freshInvoice.id, ...(freshInvoice.data || {}) };
        agencyId = freshInvoice.agency_id || agencyId;
      }
    } catch (_) {}
  }

  // Pull the agency's CURRENT settings (logo, cachet, signature, auto-stamp toggle, etc.)
  let mergedSettings = settingsRaw || {};
  try {
    if (agencyId) {
      const { data: reg } = await supabase
        .from('user_registrations')
        .select('data')
        .or(`agency_id.eq.${agencyId},uid.eq.${agencyId}`)
        .limit(1)
        .maybeSingle();
      if (reg && reg.data) {
        mergedSettings = { ...settingsRaw, ...reg.data };
      }
    }
  } catch (_) { /* fall back to passed settings */ }

  const isAr = window.currentLang === 'ar';
  const cur = (mergedSettings && mergedSettings.currency) || 'MAD';

  // Determine if this is a manual invoice or rental invoice
  const isManual = !!(doc.isManual || doc.items || doc.invoiceNumber);

  // Status mappings
  const statusLabels = {
    draft: isAr ? 'مسودة' : 'Brouillon',
    issued: isAr ? 'صادرة' : 'Émise',
    paid: isAr ? 'مدفوعة' : 'Payée',
    partial: isAr ? 'مدفوعة جزئياً' : 'Partiellement payée',
    unpaid: isAr ? 'غير مدفوعة' : 'Impayée'
  };

  const statusColors = {
    draft: '#6b7280',
    issued: '#2563eb',
    paid: '#16a34a',
    partial: '#d97706',
    unpaid: '#dc2626'
  };

  const docStatus = doc.status || (Number(doc.remainingAmount) === 0 ? 'paid' : 'issued');
  const statusLabel = statusLabels[docStatus] || (isAr ? 'فاتورة' : 'Facture');
  const statusColor = statusColors[docStatus] || '#2563eb';

  // Invoice Number
  const invoiceNum = doc.invoiceNumber || (doc.id ? String(doc.id).substring(0, 8).toUpperCase() : `FAC-${Date.now().toString().slice(-6)}`);
  
  // Dates
  const invoiceDate = doc.date || doc.invoiceDate || (doc.createdAt ? String(doc.createdAt).split('T')[0] : new Date().toISOString().split('T')[0]);
  const dueDate = doc.dueDate || '';
  const formattedInvoiceDate = formatDateFR(invoiceDate);
  const formattedDueDate = dueDate ? formatDateFR(dueDate) : '';

  // Client / Company Info
  const clientName = doc.companyName || doc.clientName || '—';
  const clientAddress = doc.companyAddress || doc.clientAddress || '—';
  const clientPhone = doc.companyPhone || doc.clientPhone || '—';
  const clientEmail = doc.companyEmail || doc.clientEmail || '—';
  const clientICE = doc.companyICE || doc.ice || '';
  const clientIF = doc.companyIF || doc.if || '';
  const clientRC = doc.companyRC || doc.rc || '';
  const clientCIN = doc.companyCin || doc.clientCin || doc.clientPassport || '';

  // Items and Amounts
  let items = [];
  let subtotalHT = 0;
  let totalDiscount = 0;
  let totalTVA = 0;
  let totalTTC = 0;
  let paidAmount = Number(doc.paidAmount) || 0;
  let remainingAmount = 0;
  const paymentMethod = doc.paymentMethod || (isAr ? 'نقدا' : 'Espèces');

  if (isManual && Array.isArray(doc.items) && doc.items.length > 0) {
    items = doc.items.map(item => {
      const q = Number(item.quantity) || 1;
      const p = Number(item.unitPrice) || 0;
      const tva = Number(item.tvaPercent) || 0;
      const d = Number(item.discountPercent) || 0;
      
      const lineHT = q * p;
      const lineDiscount = lineHT * (d / 100);
      const lineNetHT = lineHT - lineDiscount;
      const lineTVA = lineNetHT * (tva / 100);
      const lineTTC = lineNetHT + lineTVA;

      return {
        description: item.description || '',
        quantity: q,
        unitPrice: p,
        tvaPercent: tva,
        discountPercent: d,
        lineNetHT,
        lineTTC
      };
    });

    subtotalHT = Number(doc.subtotalHT) || items.reduce((sum, it) => sum + (it.quantity * it.unitPrice), 0);
    totalDiscount = Number(doc.discountAmount) || items.reduce((sum, it) => sum + (it.quantity * it.unitPrice * (it.discountPercent / 100)), 0);
    totalTVA = Number(doc.tvaAmount) || items.reduce((sum, it) => sum + (it.lineNetHT * (it.tvaPercent / 100)), 0);
    totalTTC = Number(doc.totalTTC) || (subtotalHT - totalDiscount + totalTVA);
    remainingAmount = Math.max(0, totalTTC - paidAmount);
  } else {
    // Rental legacy or single-rental invoice
    const rentalAmount = Number(doc.totalAmount) || 0;
    const depositAmount = Number(doc.deposit) || 0;
    totalTTC = rentalAmount + depositAmount;
    paidAmount = Number(doc.paidAmount) || totalTTC;
    remainingAmount = Math.max(0, totalTTC - paidAmount);
    const carName = (doc.carName || '').replace(/\s*\(.*\)\s*$/, '');
    
    items = [{
      description: `${carName || 'Location de véhicule'} (${doc.plate || doc.carPlate || ''}) - ${formatDateFR(doc.startDate)} → ${doc.isOpenContract ? '⏳' : formatDateFR(doc.endDate)}`,
      quantity: doc.days || doc.totalDays || 1,
      unitPrice: doc.dailyRate || rentalAmount,
      tvaPercent: 0,
      discountPercent: 0,
      lineNetHT: rentalAmount,
      lineTTC: rentalAmount
    }];
    if (depositAmount > 0) {
      items.push({
        description: isAr ? 'الضمان (Caution)' : 'Caution de garantie',
        quantity: 1,
        unitPrice: depositAmount,
        tvaPercent: 0,
        discountPercent: 0,
        lineNetHT: depositAmount,
        lineTTC: depositAmount
      });
    }
    subtotalHT = totalTTC;
    totalTVA = 0;
    totalDiscount = 0;
  }

  // Stamp & Signature auto insertion
  const autoOn = mergedSettings.autoStampSignature === true || mergedSettings.autoStampSignature === 'true';
  const sinceTs = mergedSettings.autoStampSince ? new Date(mergedSettings.autoStampSince).getTime() : 0;
  const docTs = doc.createdAt ? new Date(doc.createdAt).getTime() : Date.now();
  const applyAuto = autoOn && docTs >= sinceTs && (mergedSettings.companyStamp || mergedSettings.companySignature);

  const agencyStampHtml = applyAuto
    ? `<div style="display:flex;align-items:center;justify-content:center;gap:8px;width:100%;height:100%;">
         ${mergedSettings.companyStamp ? `<img src="${escapeForHtml(mergedSettings.companyStamp)}" alt="Cachet"/>` : ''}
         ${mergedSettings.companySignature ? `<img src="${escapeForHtml(mergedSettings.companySignature)}" alt="Signature"/>` : ''}
       </div>`
    : (mergedSettings.companyStamp ? `<img src="${escapeForHtml(mergedSettings.companyStamp)}" alt="Cachet"/>` : '');

  const win = window.open('', '_blank');
  if (!win) {
    return alert(isAr ? '⚠️ تم حظر النافذة المنبثقة. يرجى السماح بالنوافذ المنبثقة للطباعة.' : '⚠️ Bloqué. Autorisez les pop-ups pour imprimer.');
  }

  // Translation labels
  const tTitle = isAr ? 'فاتورة' : 'Facture';
  const tNum = isAr ? 'رقم الفاتورة' : 'N° Facture';
  const tDate = isAr ? 'تاريخ الفاتورة' : 'Date de facture';
  const tDueDate = isAr ? 'تاريخ الاستحقاق' : 'Date d\'échéance';
  const tDesc = isAr ? 'البيان / الوصف' : 'Description';
  const tQty = isAr ? 'الكمية' : 'Qté';
  const tUnitPrice = isAr ? 'سعر الوحدة' : 'Prix unit.';
  const tTva = isAr ? 'TVA' : 'TVA';
  const tDiscount = isAr ? 'خصم' : 'Remise';
  const tTotalLine = isAr ? 'المجموع' : 'Total';
  const tSubtotalHT = isAr ? 'المجموع دون ضريبة (HT)' : 'Sous-total HT';
  const tTotalDiscount = isAr ? 'إجمالي الخصم' : 'Remise';
  const tTotalTVA = isAr ? 'مجموع الضريبة (TVA)' : 'Total TVA';
  const tTotalTTC = isAr ? 'المجموع الصافي (TTC)' : 'Total TTC';
  const tAmountPaid = isAr ? 'المبلغ المؤدى' : 'Montant payé';
  const tRemaining = isAr ? 'المتبقي للأداء' : 'Reste à payer';
  const tPaymentMethod = isAr ? 'طريقة الأداء' : 'Mode de règlement';
  const tClient = isAr ? 'الزبون / الشركة' : 'Client / Société';
  const tThanks = isAr ? 'نشكركم على ثقتكم بنا' : 'Merci de votre confiance';

  win.document.write(`
<!DOCTYPE html>
<html lang="${isAr ? 'ar' : 'fr'}" dir="${isAr ? 'rtl' : 'ltr'}">
<head>
<meta charset="UTF-8">
<title>${tTitle} #${escapeForHtml(invoiceNum)} - ${escapeForHtml(clientName)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  @page { size: A4 portrait; margin: 12mm 14mm; }
  body {
    font-family: 'Helvetica Neue', Arial, sans-serif;
    font-size: 12px;
    color: #1f2937;
    line-height: 1.45;
    background: #fff;
    direction: ${isAr ? 'rtl' : 'ltr'};
    text-align: ${isAr ? 'right' : 'left'};
  }
  .invoice-container { max-width: 184mm; margin: 0 auto; }

  /* Header */
  .head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    border-bottom: 2px solid #0f172a;
    padding-bottom: 16px;
    margin-bottom: 20px;
  }
  .company .logo {
    max-height: 64px;
    max-width: 160px;
    object-fit: contain;
    margin-bottom: 8px;
    display: block;
  }
  .company .cname {
    font-size: 20px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .company .cmeta {
    font-size: 11px;
    color: #475569;
    margin-top: 4px;
    line-height: 1.6;
  }

  .doc-meta {
    text-align: ${isAr ? 'left' : 'right'};
    min-width: 180px;
  }
  .doc-meta .title {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: 2px;
    color: #0f172a;
    text-transform: uppercase;
  }
  .status-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    background-color: ${statusColor};
    margin-top: 6px;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .doc-meta table {
    margin-top: 4px;
    font-size: 11.5px;
    ${isAr ? 'margin-right:auto;' : 'margin-left:auto;'}
  }
  .doc-meta td { padding: 2px 0; }
  .doc-meta td:first-child { color: #64748b; padding-${isAr ? 'left' : 'right'}: 12px; }

  /* Client & Info Blocks */
  .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
  .box-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px 14px;
  }
  h3.sec {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #334155;
    border-bottom: 1px solid #cbd5e1;
    padding-bottom: 4px;
    margin-bottom: 8px;
  }
  table.kv { width: 100%; border-collapse: collapse; font-size: 11.5px; }
  table.kv td { padding: 3px 0; vertical-align: top; }
  table.kv td.k { color: #64748b; width: 40%; }
  table.kv td.v { font-weight: 600; color: #0f172a; }

  /* Items Grid */
  table.grid {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0 18px;
    font-size: 11.5px;
  }
  table.grid th, table.grid td {
    border: 1px solid #cbd5e1;
    padding: 8px 10px;
    text-align: ${isAr ? 'right' : 'left'};
  }
  table.grid th {
    background: #f1f5f9;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #334155;
  }
  table.grid .num { text-align: center; }
  table.grid .amt { text-align: ${isAr ? 'left' : 'right'}; white-space: nowrap; }

  /* Financial Summary & Payment Box */
  .bottom-section { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 20px; margin-top: 10px; }
  .payment-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 11.5px;
  }
  .summary-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .summary-table td { padding: 5px 0; border-bottom: 1px solid #e2e8f0; }
  .summary-table td.label { color: #64748b; }
  .summary-table td.val { text-align: ${isAr ? 'left' : 'right'}; font-weight: 600; color: #0f172a; }
  .summary-table tr.total td {
    border-top: 2px solid #0f172a;
    border-bottom: 2px solid #0f172a;
    padding: 8px 0;
    font-size: 14px;
    font-weight: 800;
    color: #0f172a;
  }
  .summary-table tr.remaining td {
    font-weight: 800;
    color: ${remainingAmount > 0 ? '#dc2626' : '#16a34a'};
  }

  /* Terms & Notes */
  .notes-box {
    margin-top: 16px;
    font-size: 10.5px;
    color: #475569;
    line-height: 1.6;
    border: 1px solid #e2e8f0;
    background: #fafafa;
    border-radius: 6px;
    padding: 8px 12px;
  }
  .notes-box b { color: #0f172a; }

  /* Signatures */
  .signs { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 16px; }
  .signbox { text-align: center; }
  .signbox .area {
    border: 1px solid #94a3b8;
    height: 88px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: #fff;
  }
  .signbox .area img { max-height: 80px; max-width: 92%; object-fit: contain; }
  .signbox .lbl {
    font-size: 10.5px;
    font-weight: 700;
    margin-top: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #334155;
  }
  .thanks {
    text-align: center;
    margin-top: 18px;
    font-size: 11px;
    color: #64748b;
    border-top: 1px solid #e2e8f0;
    padding-top: 10px;
  }

  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .status-badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="invoice-container">

    <!-- Header: Company & Invoice Meta -->
    <header class="head">
      <div class="company">
        ${mergedSettings.logo ? `<img class="logo" src="${escapeForHtml(mergedSettings.logo)}" alt="Logo"/>` : ''}
        <div class="cname">${escapeForHtml(mergedSettings.companyName || 'AGENCE DE LOCATION')}</div>
        <div class="cmeta">
          ${mergedSettings.companyAddress ? `${escapeForHtml(mergedSettings.companyAddress)}<br>` : ''}
          ${mergedSettings.companyPhone ? `${isAr ? 'الهاتف' : 'Tél'}: ${escapeForHtml(mergedSettings.companyPhone)}<br>` : ''}
          ${mergedSettings.companyEmail ? `${isAr ? 'البريد' : 'Email'}: ${escapeForHtml(mergedSettings.companyEmail)}` : ''}
          ${(mergedSettings.companyICE || mergedSettings.companyRC || mergedSettings.companyIF)
            ? `<br>${mergedSettings.companyICE ? `ICE: ${escapeForHtml(mergedSettings.companyICE)}&nbsp;&nbsp;` : ''}${mergedSettings.companyRC ? `RC: ${escapeForHtml(mergedSettings.companyRC)}&nbsp;&nbsp;` : ''}${mergedSettings.companyIF ? `IF: ${escapeForHtml(mergedSettings.companyIF)}` : ''}`
            : ''}
        </div>
      </div>
      <div class="doc-meta">
        <div class="title">${tTitle}</div>
        <div><span class="status-badge">${statusLabel}</span></div>
        <table><tbody>
          <tr><td>${tNum}</td><td><strong>${escapeForHtml(invoiceNum)}</strong></td></tr>
          <tr><td>${tDate}</td><td><strong>${escapeForHtml(formattedInvoiceDate)}</strong></td></tr>
          ${formattedDueDate ? `<tr><td>${tDueDate}</td><td><strong>${escapeForHtml(formattedDueDate)}</strong></td></tr>` : ''}
        </tbody></table>
      </div>
    </header>

    <!-- Client / Company Info -->
    <div class="cols">
      <section class="box-card">
        <h3 class="sec">${tClient}</h3>
        <table class="kv"><tbody>
          <tr><td class="k">${isAr ? 'الاسم / الشركة' : 'Nom / Raison Soc.'}</td><td class="v">${escapeForHtml(clientName)}</td></tr>
          ${clientICE ? `<tr><td class="k">ICE</td><td class="v">${escapeForHtml(clientICE)}</td></tr>` : ''}
          ${clientIF ? `<tr><td class="k">IF</td><td class="v">${escapeForHtml(clientIF)}</td></tr>` : ''}
          ${clientRC ? `<tr><td class="k">RC</td><td class="v">${escapeForHtml(clientRC)}</td></tr>` : ''}
          ${clientCIN ? `<tr><td class="k">${isAr ? 'ب.ت.و / جواز' : 'CIN / Pass'}</td><td class="v">${escapeForHtml(clientCIN)}</td></tr>` : ''}
          <tr><td class="k">${isAr ? 'الهاتف' : 'Téléphone'}</td><td class="v">${escapeForHtml(clientPhone)}</td></tr>
          <tr><td class="k">${isAr ? 'العنوان' : 'Adresse'}</td><td class="v">${escapeForHtml(clientAddress)}</td></tr>
          ${clientEmail && clientEmail !== '—' ? `<tr><td class="k">${isAr ? 'البريد' : 'Email'}</td><td class="v">${escapeForHtml(clientEmail)}</td></tr>` : ''}
        </tbody></table>
      </section>

      <section class="box-card">
        <h3 class="sec">${isAr ? 'معلومات الفاتورة والأداء' : 'Informations de facturation'}</h3>
        <table class="kv"><tbody>
          <tr><td class="k">${tPaymentMethod}</td><td class="v">${escapeForHtml(paymentMethod)}</td></tr>
          <tr><td class="k">${isAr ? 'العملة' : 'Devise'}</td><td class="v">${cur}</td></tr>
          <tr><td class="k">${isAr ? 'عدد البنود' : 'Nombre d\'articles'}</td><td class="v">${items.length}</td></tr>
          <tr><td class="k">${isAr ? 'حالة السداد' : 'État de paiement'}</td><td class="v" style="color:${statusColor};">${statusLabel}</td></tr>
        </tbody></table>
      </section>
    </div>

    <!-- Items Grid Table -->
    <h3 class="sec">${isAr ? 'تفاصيل الفاتورة' : 'Détails des prestations / articles'}</h3>
    <table class="grid">
      <thead>
        <tr>
          <th style="width: ${isManual ? '42%' : '48%'};">${tDesc}</th>
          <th class="num" style="width: 10%;">${tQty}</th>
          <th class="amt" style="width: 16%;">${tUnitPrice} (${cur})</th>
          ${isManual ? `<th class="num" style="width: 10%;">${tTva}</th>` : ''}
          ${isManual ? `<th class="num" style="width: 10%;">${tDiscount}</th>` : ''}
          <th class="amt" style="width: 16%;">${tTotalLine} (${cur})</th>
        </tr>
      </thead>
      <tbody>
        ${items.map(item => `
          <tr>
            <td><strong>${escapeForHtml(item.description || '—')}</strong></td>
            <td class="num">${item.quantity}</td>
            <td class="amt">${Number(item.unitPrice).toFixed(2)}</td>
            ${isManual ? `<td class="num">${item.tvaPercent ? `${item.tvaPercent}%` : '0%'}</td>` : ''}
            ${isManual ? `<td class="num">${item.discountPercent ? `${item.discountPercent}%` : '—'}</td>` : ''}
            <td class="amt">${Number(item.lineTTC || item.lineNetHT || (item.quantity * item.unitPrice)).toFixed(2)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <!-- Financial Breakdown & Payment Summary -->
    <div class="bottom-section">
      <div class="payment-box">
        <h3 class="sec">${isAr ? 'ملاحظات وشروط الأداء' : 'Conditions & Règlements'}</h3>
        <p style="font-size:11px; line-height:1.6; color:#475569;">
          ${doc.notes ? escapeForHtml(doc.notes) : (isAr 
            ? 'يرجى تسديد الفاتورة في الآجال المحددة. في حالة التأخير تطبق الفوائد القانونية الجاري بها العمل.' 
            : 'Paiement à réception de la facture ou selon la date d\'échéance convenue. Merci pour votre confiance.')}
        </p>
      </div>

      <div>
        <table class="summary-table">
          <tbody>
            <tr>
              <td class="label">${tSubtotalHT}</td>
              <td class="val">${subtotalHT.toFixed(2)} ${cur}</td>
            </tr>
            ${totalDiscount > 0 ? `
              <tr>
                <td class="label">${tTotalDiscount}</td>
                <td class="val" style="color:#dc2626;">-${totalDiscount.toFixed(2)} ${cur}</td>
              </tr>
            ` : ''}
            ${totalTVA > 0 ? `
              <tr>
                <td class="label">${tTotalTVA}</td>
                <td class="val">+${totalTVA.toFixed(2)} ${cur}</td>
              </tr>
            ` : ''}
            <tr class="total">
              <td class="label">${tTotalTTC}</td>
              <td class="val">${totalTTC.toFixed(2)} ${cur}</td>
            </tr>
            <tr>
              <td class="label">${tAmountPaid}</td>
              <td class="val" style="color:#16a34a;">${paidAmount.toFixed(2)} ${cur}</td>
            </tr>
            <tr class="remaining">
              <td class="label">${tRemaining}</td>
              <td class="val">${remainingAmount.toFixed(2)} ${cur}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Footer: Stamp & Signatures -->
    <div class="signs">
      <div class="signbox">
        <div class="area">${agencyStampHtml}</div>
        <div class="lbl">${isAr ? 'ختم وتوقيع الوكالة' : 'Cachet & Signature (Agence)'}</div>
      </div>
      <div class="signbox">
        <div class="area">
          ${doc.clientSignature ? `<img src="${escapeForHtml(doc.clientSignature)}" alt="Signature client"/>` : ''}
        </div>
        <div class="lbl">${isAr ? 'توقيع الزبون / الشركة' : 'Signature Client / Société'}</div>
      </div>
    </div>

    <div class="thanks">${tThanks} — ${escapeForHtml(mergedSettings.companyName || '')} · ${escapeForHtml(formattedInvoiceDate)}</div>
  </div>
</body>
</html>
  `);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 350);
};
